import { clampByte, cloneBuffer, type PixelBuffer, premultiply, unpremultiply } from './buffer';

/**
 * Non-pointwise effects that are neither pointwise adjustments nor convolutions.
 *
 * All three are deterministic: the preview re-runs the whole filter on every
 * slider tick, so a filter that used `Math.random()` would look like TV static
 * and no test could ever assert on it. Noise derives its randomness from the
 * pixel coordinate instead, which makes it stable under re-running.
 */

/**
 * Deterministic hash of a pixel coordinate, returning 0..1.
 *
 * Cheap integer mixing (Murmur-ish finaliser). Good enough for grain — this is
 * not a cryptographic primitive, it just has to look scattered and repeat
 * exactly for the same (x, y).
 */
function hashPixel(x: number, y: number): number {
  let h = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 4294967295;
}

/**
 * Add monochrome grain. `amount` is 0..1; 1 swings each pixel by roughly ±100.
 *
 * The same offset is applied to all three channels so grain reads as film noise
 * rather than colour speckle, and alpha is left alone so transparent regions
 * stay transparent instead of gaining visible speckles.
 */
export function noiseBuffer(buffer: PixelBuffer, amount: number): void {
  const strength = Math.max(0, Math.min(1, amount)) * 100;
  if (strength === 0) return;
  const { data, width } = buffer;
  for (let y = 0; y < buffer.height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (data[i + 3] === 0) continue;
      // -1..1, biased to zero so most pixels move a little and few move a lot.
      const r = hashPixel(x, y) * 2 - 1;
      const s = Math.sign(r) * Math.abs(r) ** 0.7 * strength;
      data[i] = clampByte((data[i] ?? 0) + s);
      data[i + 1] = clampByte((data[i + 1] ?? 0) + s);
      data[i + 2] = clampByte((data[i + 2] ?? 0) + s);
    }
  }
}

/**
 * Darken towards the edges. `strength` is 0..1.
 *
 * Distance is normalised so the corners are exactly 1, which keeps the look
 * identical for any aspect ratio instead of hugging wide images into bands.
 * Falloff is quadratic: flat across the middle, then a clear roll-off, which is
 * what a lens vignette actually does.
 */
export function vignetteBuffer(buffer: PixelBuffer, strength: number): void {
  const amount = Math.max(0, Math.min(1, strength));
  if (amount === 0) return;
  const { data, width, height } = buffer;
  const cx = width > 1 ? width - 1 : 1;
  const cy = height > 1 ? height - 1 : 1;
  for (let y = 0; y < height; y++) {
    const ny = (y / cy) * 2 - 1;
    for (let x = 0; x < width; x++) {
      const nx = (x / cx) * 2 - 1;
      const d = Math.min(1, Math.sqrt(nx * nx + ny * ny) / Math.SQRT2);
      const factor = 1 - amount * d * d;
      const i = (y * width + x) * 4;
      data[i] = clampByte((data[i] ?? 0) * factor);
      data[i + 1] = clampByte((data[i + 1] ?? 0) * factor);
      data[i + 2] = clampByte((data[i + 2] ?? 0) * factor);
    }
  }
}

/**
 * Mosaic blocks of `blockSize` pixels. `blockSize` < 2 leaves the buffer alone.
 *
 * Averaging happens on premultiplied colour, for the same reason blur does:
 * averaging straight RGB drags the colour of transparent pixels into visible
 * ones and gives every edge a dark fringe.
 */
export function pixelateBuffer(buffer: PixelBuffer, blockSize: number): PixelBuffer {
  const size = Math.floor(blockSize);
  if (size < 2) return cloneBuffer(buffer);
  const out = cloneBuffer(buffer);
  premultiply(out);
  const src = cloneBuffer(out);
  const { data, width, height } = out;
  const s = src.data;
  for (let top = 0; top < height; top += size) {
    const bottom = Math.min(top + size, height);
    for (let left = 0; left < width; left += size) {
      const right = Math.min(left + size, width);
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      for (let y = top; y < bottom; y++) {
        for (let x = left; x < right; x++) {
          const i = (y * width + x) * 4;
          r += s[i] ?? 0;
          g += s[i + 1] ?? 0;
          b += s[i + 2] ?? 0;
          a += s[i + 3] ?? 0;
        }
      }
      const count = (bottom - top) * (right - left);
      // Still premultiplied, which is exactly what `out` is; the single
      // unpremultiply at the end divides the colour back out once.
      const ar = r / count;
      const ag = g / count;
      const ab = b / count;
      const aa = a / count;
      for (let y = top; y < bottom; y++) {
        for (let x = left; x < right; x++) {
          const i = (y * width + x) * 4;
          data[i] = clampByte(ar);
          data[i + 1] = clampByte(ag);
          data[i + 2] = clampByte(ab);
          data[i + 3] = clampByte(aa);
        }
      }
    }
  }
  unpremultiply(out);
  return out;
}
